/**
 * Generic Ant Design table wrapper with server-side pagination wired
 * to TanStack Query. Handles the `{total, rows}` envelope our list
 * routes return.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Table, type TableProps } from 'antd';
import { useState, useMemo, useEffect } from 'react';
import { api } from '../api/client';

interface DataTableProps<T> extends Omit<TableProps<T>, 'dataSource' | 'pagination'> {
  queryKey: unknown[];
  endpoint: string;
  extraParams?: Record<string, unknown>;
  rowKey?: keyof T | ((r: T) => React.Key);
}

interface ListResult<T> {
  total: number;
  rows: T[];
}

export function DataTable<T extends object>(props: DataTableProps<T>) {
  const { queryKey, endpoint, extraParams, rowKey = 'id', ...tableProps } = props;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const params = useMemo(() => ({ page, limit, ...extraParams }), [page, limit, extraParams]);

  // Reset to page 1 when filters change (extraParams identity), not when only page/limit change.
  const filterKey = useMemo(() => JSON.stringify(extraParams ?? {}), [extraParams]);
  useEffect(() => {
    setPage(1);
  }, [filterKey]);

  const { data, isFetching } = useQuery({
    queryKey: [...queryKey, params],
    queryFn: () => api.get<ListResult<T>>(endpoint, params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  return (
    <Table<T>
      {...tableProps}
      className="uo-data-table"
      size="small"
      rowKey={rowKey as never}
      bordered
      dataSource={data?.rows ?? []}
      loading={isFetching}
      pagination={{
        current: page,
        pageSize: limit,
        total: data?.total ?? 0,
        showSizeChanger: true,
        showTotal: (t) => `共 ${t} 条`,
        onChange: (p, s) => {
          setPage(p);
          setLimit(s);
        },
      }}
    />
  );
}
